import { InputMask } from "@react-input/mask";
import Header from "@/atomic/atoms/Header";
import { PropsView } from "./Models";
import Button from "@/atomic/atoms/Button";
import Loading from "@/atomic/atoms/Loading";
import Modal from "@/atomic/atoms/Modal";
import { Controller } from "react-hook-form";
import { Suspense, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { setCustomer } from "@/redux/ducks/customer";
import { ReducersTypes } from "@/redux/reducers/Models";

const selectBase =
  "w-full h-14 px-4 pr-10 rounded-xl border font-primary text-base font-semibold text-text bg-background appearance-none cursor-pointer " +
  "focus:outline-none focus:ring-0 focus:border-primary";

const inputBase =
  "w-full h-14 px-4 rounded-xl border font-primary text-base font-semibold text-text bg-background uppercase " +
  "focus:outline-none focus:ring-0 focus:border-primary";

function fieldClass(hasError?: boolean) {
  return [selectBase, hasError ? "border-error" : "border-border"].join(" ");
}

function inputFieldClass(hasError?: boolean) {
  return [inputBase, hasError ? "border-error" : "border-border"].join(" ");
}

const chevronIcon = (
  <svg
    className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-text pointer-events-none"
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

export default function BuyVehicleView({
  goBack,
  isPJEnabled,
  onChangeSelect,
  isDisabled,
  types,
  brands,
  models,
  errorPlate,
  isLoading,
  error,
  handlePlate,
  errorCreateVehicle,
  success,
  register,
  handleSubmit,
  errors,
  isVisibleModal,
  toggleModal,
  control,
  isVisibleBulkModal,
  toggleBulkModal,
  selectedFile,
  onFileChange,
  isBulkLoading,
  bulkProgress,
  bulkResult,
  onBulkSubmit,
  onBulkReset,
}: PropsView) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const dispatch = useDispatch();
  const customer = useSelector((state: ReducersTypes) => state.customerReducer);

  return (
    <div className="h-full relative flex flex-col bg-white overflow-hidden">
      <Header
        title="Dados do veículo"
        hasLeftButton
        goBack={goBack}
      />
      <main className="flex-1 w-full flex justify-center min-h-0 bg-background">
        <form
          className="w-full max-w-[480px] px-6 pt-6 pb-8 flex flex-col flex-1 min-h-0"
          onSubmit={handleSubmit}
        >
          <div className="flex-1 min-h-0 overflow-y-auto pb-4">
            <h2 className="text-text text-[22px] leading-tight font-primary font-semibold tracking-tight">
              Dados do novo veículo
            </h2>

            <div className="mt-6 flex flex-col gap-5">
              <div>
                <label className="block text-sm font-primary text-text mb-2">
                  Placa<span className="text-error">*</span>
                </label>
                <InputMask
                  {...register("plate")}
                  name="plate"
                  placeholder="Placa"
                  className={inputFieldClass(!!errors.plate)}
                  mask="___-.@.."
                  replacement={{ _: /\D/, ".": /\d/, "@": /[A-Za-z0-9]*/ }}
                  type="text"
                />
                {errors.plate && (
                  <span className="mt-1 block text-error text-xs font-primary">
                    {errors.plate.message as string}
                  </span>
                )}
              </div>

              <div>
                <label className="block text-sm font-primary text-text mb-2">
                  Tipo do veículo
                </label>
                <div className="relative">
                  <Controller
                    control={control}
                    name="type"
                    rules={{ required: true }}
                    render={({ field: { onChange, value } }) => (
                      <select
                        onChange={e => onChangeSelect({ e, onChange })}
                        value={value}
                        title="Tipo do veículo"
                        name="type"
                        className={fieldClass(!!errors.type)}
                      >
                        <option value="0">Tipo do veículo</option>
                        {types?.map((item, index) => (
                          <option key={index} value={item.id}>{item.text}</option>
                        ))}
                      </select>
                    )}
                  />
                  {chevronIcon}
                </div>
                {errors.type && (
                  <span className="mt-1 block text-error text-xs font-primary">
                    {errors.type.message as string}
                  </span>
                )}
              </div>

              <div>
                <label className="block text-sm font-primary text-text mb-2">
                  Marca
                </label>
                <div className="relative">
                  <Controller
                    control={control}
                    name="brand"
                    rules={{ required: true }}
                    render={({ field: { onChange, value } }) => (
                      <select
                        onChange={e => onChangeSelect({ e, onChange })}
                        value={value}
                        title="Marca"
                        name="brand"
                        className={fieldClass(!!errors.brand)}
                      >
                        <option value="0">Marca</option>
                        {brands?.map((item, index) => (
                          <option key={index} value={item.value}>{item.text}</option>
                        ))}
                      </select>
                    )}
                  />
                  {chevronIcon}
                </div>
                {errors.brand && (
                  <span className="mt-1 block text-error text-xs font-primary">
                    {errors.brand.message as string}
                  </span>
                )}
              </div>

              <div>
                <label className="block text-sm font-primary text-text mb-2">
                  Modelo
                </label>
                <div className="relative">
                  <Controller
                    control={control}
                    name="model"
                    rules={{ required: true }}
                    render={({ field: { onChange, value } }) => (
                      <select
                        onChange={e => onChangeSelect({ e, onChange })}
                        value={value}
                        title="Modelo"
                        name="model"
                        className={fieldClass(!!errors.model)}
                      >
                        <option value="0">Modelo</option>
                        {models?.map((item, index) => (
                          <option key={index} value={item.value}>{item.text}</option>
                        ))}
                      </select>
                    )}
                  />
                  {chevronIcon}
                </div>
                {errors.model && (
                  <span className="mt-1 block text-error text-xs font-primary">
                    {errors.model.message as string}
                  </span>
                )}
              </div>
            </div>
          </div>

          <div className="mt-8 shrink-0 flex flex-col gap-3">
            <Button
              className="rounded-theme-large bg-primary text-white font-primary font-semibold border-0"
              title="Continuar"
              type="submit"
              isDisabled={isDisabled}
            />
            {isPJEnabled && (
              <button
                type="button"
                onClick={toggleBulkModal}
                className="text-text text-base font-primary font-medium text-center py-2"
              >
                Mais veículos
              </button>
            )}
          </div>
        </form>
      </main>
      {error && isVisibleModal && <Suspense fallback={null}>
        <Modal
          title="Ops!"
          message="Houve um problema ao validar sua placa, quer tentar novamente?"
          actionMessage="Tentar novamente"
          action={() => {
            handlePlate();
            toggleModal();
          }}
          actionSecondMessage="Não!"
          actionSecond={toggleModal}
        />
      </Suspense>}
      {errorPlate && isVisibleModal && <Suspense fallback={null}>
        <Modal
          title="Atenção!"
          message="Esta placa é inválida."
          actionMessage="Entendi"
          action={toggleModal}
          actionSecond={toggleModal}
        />
      </Suspense>}
      {errorCreateVehicle && isVisibleModal && <Suspense fallback={null}>
        <Modal
          title="Ops!"
          message="Houve um problema ao incluir o veículo, quer tentar novamente?"
          actionMessage="Sim"
          action={() => {
            handleSubmit();
            toggleModal();
          }}
          actionSecondMessage="Não"
          actionSecond={toggleModal}
        />
      </Suspense>}
      {success && isVisibleModal && <Suspense fallback={null}>
        <Modal
          title="Muito bem!"
          message="Veículo adicionado com sucesso ao cadastro."
          actionMessage="Atualizar"
          action={() => {
            const newVehicle = customer?.vehicles?.[customer.vehicles.length - 1];
            if (newVehicle?.plate) {
              dispatch(setCustomer({ 
                selectedVehicles: [newVehicle.plate]
              }));
            }
            goBack();
            toggleModal();
          }}
        />
      </Suspense>}
      {isLoading && <Loading />}
      {isPJEnabled && isVisibleBulkModal && <Suspense fallback={null}>
        <Modal
          content={
            <div className="flex flex-col font-primary">
              <div className="absolute top-3 left-1/2 transform -translate-x-1/2 w-10 h-1 bg-border rounded-full" />
              {!isBulkLoading && (
                <button
                  type="button"
                  onClick={bulkResult ? onBulkReset : toggleBulkModal}
                  className="absolute top-6 right-6 text-text"
                >
                  <img src="/svg/icon-close.svg" alt="Fechar" className="w-6 h-6" />
                </button>
              )}

              {isBulkLoading ? (
                <div className="mt-6 mb-2">
                  <p className="text-text text-xl font-bold text-center mb-8">Processando...</p>
                  <div className="w-full bg-border rounded-full h-2 mb-2">
                    <div
                      className="bg-primary h-2 rounded-full transition-all duration-300 ease-in-out"
                      style={{ width: `${bulkProgress}%` }}
                    />
                  </div>
                  <div className="flex justify-between mt-1">
                    <span className="text-text text-sm font-bold truncate max-w-[60%]">{selectedFile?.name}</span>
                    <span className="text-text text-sm">{bulkProgress}% carregando</span>
                  </div>
                </div>
              ) : bulkResult ? (
                <div className="mt-6 mb-2">
                  <p className="text-text text-xl font-bold text-center mb-4">
                    {bulkResult.code === 1 ? 'Sucesso!' : 'Atenção!'}
                  </p>
                  <p className="text-text text-sm mb-4 text-center">
                    {bulkResult.message || (bulkResult.code === 1
                      ? 'Todas as placas foram cadastradas com sucesso.'
                      : 'Houve um problema ao processar as placas.')}
                  </p>
                  {bulkResult.errors?.length > 0 && (
                    <div className="max-h-40 overflow-y-auto mb-4 border border-border rounded-xl p-3 space-y-2">
                      {bulkResult.errors.map((err, i) => (
                        <div key={i} className="text-xs text-error">
                          Linha {err.line}: Placa <strong>{err.plate}</strong> — {err.reason}
                        </div>
                      ))}
                    </div>
                  )}
                  <button
                    type="button"
                    onClick={() => { onBulkReset(); toggleBulkModal(); }}
                    className="w-full bg-primary text-white rounded-xl py-4 text-base font-semibold mt-2"
                  >
                    Fechar
                  </button>
                </div>
              ) : (
                <>
                  <p className="text-text text-xl font-bold text-center mt-6 mb-5">Anexar</p>
                  <div className="text-text text-sm mb-5 space-y-3">
                    <p className="font-bold">Orientações para importação da planilha:</p>
                    <p>
                      Coluna A: <strong>Placa</strong> - Deve conter de 5 a 7 posições contendo apenas letras e números. Não utilize caracteres especiais (!@#$%¨&amp;*()_{}?/;,-)
                    </p>
                    <p>Você poderá fazer o upload de até 200 placas a cada arquivo.</p>
                    <p>
                      Baixe a planilha{" "}
                      <a href="/Cadastro Massivo de Placas.xlsx" download className="underline font-semibold text-primary">
                        clicando aqui
                      </a>
                      , preencha os dados e faça upload através do botão abaixo.
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="w-full bg-background border border-dashed border-border rounded-xl flex flex-col items-center justify-center gap-2 py-6 mb-5"
                  >
                    {selectedFile ? (
                      <span className="text-text text-sm font-semibold px-2 text-center break-all">{selectedFile.name}</span>
                    ) : (
                      <>
                        <span className="w-8 h-8 rounded-full border-2 border-text flex items-center justify-center text-text text-xl font-light">+</span>
                        <span className="text-tertiary text-sm">
                          Arraste ou{" "}
                          <span className="underline font-semibold text-text">clique aqui</span>
                          {" "}para enviar
                        </span>
                      </>
                    )}
                  </button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".xls,.xlsx"
                    className="hidden"
                    onChange={e => {
                      onFileChange(e.target.files?.[0] ?? null);
                      e.target.value = '';
                    }}
                  />
                  <button
                    type="button"
                    onClick={onBulkSubmit}
                    disabled={!selectedFile}
                    className="w-full bg-primary text-white rounded-xl py-4 text-base font-semibold disabled:opacity-40"
                  >
                    Atualizar
                  </button>
                </>
              )}
            </div>
          }
        />
      </Suspense>}
    </div>
  );
}
